import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, User, Video, Phone } from "lucide-react";
import { format, addDays, startOfWeek, isSameDay, parseISO, isBefore } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getBrowserTimezone, getTimezoneAbbr, formatInTz } from "@/lib/timezone";

interface AvailabilitySlot {
  id: string;
  day_of_week: number | null;
  specific_date: string | null;
  start_time: string;
  end_time: string;
}

interface AppointmentBlock {
  id: string;
  patient_id: string;
  start_time: string;
  end_time: string;
  status: string;
  call_type: string;
  meeting_url: string | null;
  patient_first_name?: string | null;
  patient_last_name?: string | null;
}

// 15-minute slot granularity: 7 AM to 8 PM = 52 slots
const SLOTS: { hour: number; minute: number }[] = [];
for (let h = 7; h <= 20; h++) {
  for (let m = 0; m < 60; m += 15) {
    if (h === 20 && m > 0) break;
    SLOTS.push({ hour: h, minute: m });
  }
}
const SLOT_HEIGHT = 20; // px per 15-min slot

const formatSlotLabel = (hour: number, minute: number) => {
  if (minute !== 0) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${h} ${ampm}`;
};

const ProviderCalendarView = () => {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [appointments, setAppointments] = useState<AppointmentBlock[]>([]);

  const tz = useMemo(() => getBrowserTimezone(), []);
  const tzAbbr = useMemo(() => getTimezoneAbbr(tz), [tz]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user, weekStart]);

  const fetchData = async () => {
    if (!user) return;
    const weekEnd = addDays(weekStart, 7);

    const [availRes, apptRes] = await Promise.all([
      supabase
        .from("provider_availability")
        .select("id, day_of_week, specific_date, start_time, end_time")
        .eq("provider_id", user.id),
      supabase
        .from("appointments")
        .select("id, patient_id, start_time, end_time, status, call_type, meeting_url")
        .eq("provider_id", user.id)
        .gte("start_time", weekStart.toISOString())
        .lt("start_time", weekEnd.toISOString())
        .neq("status", "cancelled"),
    ]);

    if (availRes.data) setAvailability(availRes.data as AvailabilitySlot[]);

    if (apptRes.data && apptRes.data.length > 0) {
      const patientIds = [...new Set(apptRes.data.map((a: any) => a.patient_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name")
        .in("user_id", patientIds);
      const profileMap = new Map(profiles?.map((p: any) => [p.user_id, p]) || []);
      setAppointments(
        apptRes.data.map((a: any) => {
          const profile = profileMap.get(a.patient_id);
          return {
            ...a,
            patient_first_name: profile?.first_name,
            patient_last_name: profile?.last_name,
          };
        })
      );
    } else {
      setAppointments([]);
    }
  };

  // Convert time string "HH:MM" to fractional hours from 7 AM, then to pixels
  const PX_PER_HOUR = SLOT_HEIGHT * 4;
  const timeToPixels = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    return (h - 7 + m / 60) * PX_PER_HOUR;
  };

  // Convert ISO date to fractional hours from 7 AM
  const isoToPixels = (iso: string) => {
    const d = parseISO(iso);
    return (d.getHours() - 7 + d.getMinutes() / 60) * PX_PER_HOUR;
  };

  const isToday = (date: Date) => isSameDay(date, new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarDays size={20} className="text-primary" />
          <h2 className="font-display text-xl text-foreground">Weekly Schedule</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWeekStart((d) => addDays(d, -7))}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-medium text-foreground min-w-[180px] text-center">
            {format(weekDays[0], "MMM d")} – {format(weekDays[6], "MMM d, yyyy")}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setWeekStart((d) => addDays(d, 7))}>
            <ChevronRight size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs ml-2"
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }))}
          >
            Today
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-primary/15 border border-primary/30" /> Available
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-primary border border-primary" /> Booked
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-green-500/20 border border-green-500/40" /> Completed
        </span>
      </div>

      <Card className="border-border bg-card overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[700px]">
              {/* Day headers */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border">
                <div className="p-2" />
                {weekDays.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`p-2 text-center border-l border-border ${
                      isToday(day)
                        ? "bg-primary/5"
                        : ""
                    }`}
                  >
                    <p className="text-xs font-medium text-muted-foreground">{format(day, "EEE")}</p>
                    <p
                      className={`text-lg font-bold ${
                        isToday(day) ? "text-primary" : "text-foreground"
                      }`}
                    >
                      {format(day, "d")}
                    </p>
                  </div>
                ))}
              </div>

              {/* Time grid */}
              <div className="relative">
                <div className="grid grid-cols-[60px_repeat(7,1fr)]">
                  {SLOTS.map((slot, i) => {
                    const label = formatSlotLabel(slot.hour, slot.minute);
                    const isHour = slot.minute === 0;
                    return (
                      <div key={i} className="contents">
                        {/* Time label */}
                        <div style={{ height: SLOT_HEIGHT }} className="flex items-start justify-end pr-2 pt-0.5">
                          {isHour && (
                            <span className="text-[10px] text-muted-foreground">{label}</span>
                          )}
                        </div>
                        {/* Day columns */}
                        {weekDays.map((day) => (
                          <div
                            key={`${i}-${day.toISOString()}`}
                            className={`border-l ${isHour ? "border-t border-border" : "border-t border-border/20"} relative ${
                              isToday(day) ? "bg-primary/[0.02]" : ""
                            }`}
                            style={{ height: SLOT_HEIGHT }}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* Availability overlay */}
                <div className="absolute inset-0 grid grid-cols-[60px_repeat(7,1fr)] pointer-events-none">
                  <div />
                  {weekDays.map((day, dayIdx) => {
                    const dayOfWeek = day.getDay();
                    const dateStr = format(day, "yyyy-MM-dd");
                    // Specific-date availability for this day
                    const dateAvail = availability.filter((a) => a.specific_date === dateStr);
                    // Recurring template availability (only show if no specific-date slots)
                    const templateAvail = dateAvail.length === 0
                      ? availability.filter((a) => a.day_of_week === dayOfWeek && !a.specific_date)
                      : [];
                    const allAvail = [...dateAvail, ...templateAvail];
                    const dayAppts = appointments.filter((a) =>
                      isSameDay(parseISO(a.start_time), day)
                    );

                    return (
                      <div key={dayIdx} className="relative border-l border-border">
                        {/* Availability blocks */}
                        {allAvail.map((slot) => {
                          const st = slot.start_time.slice(0, 5);
                          const et = slot.end_time.slice(0, 5);
                          const top = timeToPixels(st);
                          const height = timeToPixels(et) - top;
                          if (height <= 0) return null;
                          const isTemplate = !slot.specific_date;
                          return (
                            <div
                              key={slot.id}
                              className={`absolute left-0.5 right-0.5 rounded-md ${
                                isTemplate
                                  ? "bg-primary/10 border border-dashed border-primary/20"
                                  : "bg-primary/10 border border-primary/20"
                              }`}
                              style={{ top: `${top}px`, height: `${height}px` }}
                            />
                          );
                        })}

                        {/* Appointment blocks */}
                        <TooltipProvider>
                          {dayAppts.map((appt) => {
                            const top = isoToPixels(appt.start_time);
                            const height =
                              isoToPixels(appt.end_time) - top;
                            if (height <= 0 || top < 0) return null;
                            const patientName = appt.patient_first_name
                              ? `${appt.patient_first_name} ${appt.patient_last_name || ""}`.trim()
                              : "Patient";
                            const isCompleted = appt.status === "completed";
                            const isRescheduled = appt.status === "rescheduled";
                            const isNoShow = appt.status === "no_show";
                            const isCancelled = appt.status === "cancelled";
                            const isPast = isBefore(parseISO(appt.end_time), new Date());
                            return (
                              <Tooltip key={appt.id}>
                                <TooltipTrigger asChild>
                                  <div
                                    className={`absolute left-1 right-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium cursor-default pointer-events-auto overflow-hidden ${
                                      isCompleted
                                        ? "bg-green-500/20 border border-green-500/40 text-green-800"
                                        : isRescheduled
                                          ? "bg-blue-500/20 border border-blue-500/40 text-blue-800"
                                        : isNoShow
                                          ? "bg-red-500/20 border border-red-500/40 text-red-800"
                                        : isCancelled
                                          ? "bg-muted border border-border text-muted-foreground line-through"
                                        : isPast
                                          ? "bg-muted border border-border text-muted-foreground"
                                          : "bg-primary text-primary-foreground border border-primary"
                                    }`}
                                    style={{ top: `${top}px`, height: `${Math.max(height, 16)}px` }}
                                  >
                                    <span className="truncate block leading-tight">
                                      {formatInTz(appt.start_time, tz, "h:mm a")}
                                    </span>
                                    {height >= 28 && (
                                      <span className="truncate block leading-tight opacity-80">
                                        {patientName}
                                      </span>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="text-xs">
                                  <p className="font-medium">{patientName}</p>
                                  <p>
                                    {formatInTz(appt.start_time, tz, "h:mm a")} –{" "}
                                    {formatInTz(appt.end_time, tz, "h:mm a")} {tzAbbr}
                                  </p>
                                  <p className="flex items-center gap-1 mt-0.5">
                                    {appt.call_type === "audio" ? (
                                      <Phone size={10} />
                                    ) : (
                                      <Video size={10} />
                                    )}
                                    {appt.call_type === "video" ? "Video" : "Audio"} ·{" "}
                                    <Badge
                                      variant="outline"
                                      className="text-[9px] px-1 py-0 capitalize"
                                    >
                                      {appt.status}
                                    </Badge>
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </TooltipProvider>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProviderCalendarView;
